/**
 * Dashboard widget registry — every widget has type, default size (w, h), category, and optional config schema.
 * Used by the grid engine and widget picker.
 */

export const WIDGET_CATEGORIES = {
  financial: { id: 'financial', label: 'Financial', icon: '💰' },
  property: { id: 'property', label: 'Property & Units', icon: '🏠' },
  tenant: { id: 'tenant', label: 'Tenant & Leasing', icon: '👥' },
  maintenance: { id: 'maintenance', label: 'Maintenance', icon: '🔧' },
  legal: { id: 'legal', label: 'Legal & Compliance', icon: '⚖️' },
  productivity: { id: 'productivity', label: 'Productivity', icon: '📅' },
};

/** Default grid: 12 cols. w = columns, h = row height units (rowHeight ~60px). */
const defaultSize = (w, h) => ({ w: w ?? 4, h: h ?? 2 });
const fullWidth = (h) => ({ w: 12, h: h ?? 2 });

export const WIDGET_REGISTRY = {
  // —— Productivity (search, actions, tasks, calendar, AI) ——
  search: {
    id: 'search',
    label: 'Search Bar',
    category: 'productivity',
    ...fullWidth(2),
    type: 'full',
    description: 'Global search and AI assistant',
  },
  'quick-actions': {
    id: 'quick-actions',
    label: 'Quick Actions',
    category: 'productivity',
    ...fullWidth(2),
    type: 'full',
    description: 'Shortcut buttons for common tasks',
  },
  'upcoming-tasks': {
    id: 'upcoming-tasks',
    label: 'Upcoming Tasks',
    category: 'productivity',
    ...defaultSize(4, 5),
    type: 'panel',
    description: 'Prioritized to-do list',
  },
  'activity-feed': {
    id: 'activity-feed',
    label: 'Activity Feed',
    category: 'productivity',
    ...defaultSize(4, 3),
    type: 'panel',
    description: 'Audit log of recent actions',
    implemented: false,
  },
  calendar: {
    id: 'calendar',
    label: 'Calendar',
    category: 'productivity',
    ...defaultSize(6, 4),
    type: 'panel',
    description: 'Leases, maintenance, tasks',
    implemented: false,
  },

  // —— Financial ——
  revenue: {
    id: 'revenue',
    label: 'Monthly Revenue Summary',
    category: 'financial',
    ...defaultSize(3, 3),
    type: 'card',
    icon: '💰',
    iconClass: 'revenue-icon',
    description: 'Rent collected vs expected',
  },
  'rent-roll': {
    id: 'rent-roll',
    label: 'Rent Roll',
    category: 'financial',
    ...defaultSize(6, 4),
    type: 'panel',
    description: 'Units, tenants, rent, payment status',
    implemented: false,
  },
  'expense-tracker': {
    id: 'expense-tracker',
    label: 'Expense Tracker',
    category: 'financial',
    ...defaultSize(4, 3),
    type: 'panel',
    description: 'Operating costs by category',
    implemented: false,
  },
  'payment-history': {
    id: 'payment-history',
    label: 'Payment History Feed',
    category: 'financial',
    ...defaultSize(4, 3),
    type: 'panel',
    description: 'Recent payments received',
    implemented: false,
  },
  'upcoming-invoices': {
    id: 'upcoming-invoices',
    label: 'Upcoming Invoices',
    category: 'financial',
    ...defaultSize(4, 2),
    type: 'panel',
    description: 'Bills due in next 30 days',
    implemented: false,
  },

  // —— Property & Units ——
  properties: {
    id: 'properties',
    label: 'Properties',
    category: 'property',
    ...defaultSize(3, 3),
    type: 'card',
    icon: '🏢',
    iconClass: 'properties-icon',
    description: 'Portfolio count, quick links',
  },
  'properties-db': {
    id: 'properties-db',
    label: 'Properties Database',
    category: 'property',
    ...fullWidth(6),
    type: 'full',
    description: 'Full properties table with search and actions',
  },
  'portfolio-overview': {
    id: 'portfolio-overview',
    label: 'Portfolio Overview',
    category: 'property',
    ...defaultSize(6, 3),
    type: 'panel',
    description: 'All properties, occupancy %, unit count',
    implemented: false,
  },
  'occupancy-rate': {
    id: 'occupancy-rate',
    label: 'Occupancy Rate',
    category: 'property',
    ...defaultSize(3, 2),
    type: 'card',
    description: '% occupied, trend',
    implemented: false,
  },
  'move-in-out': {
    id: 'move-in-out',
    label: 'Move-In / Move-Out Calendar',
    category: 'property',
    ...defaultSize(6, 5),
    type: 'panel',
    icon: '📦',
    description: 'Upcoming lease starts and ends',
  },

  // —— Tenant & Leasing ——
  applications: {
    id: 'applications',
    label: 'Active Applications',
    category: 'tenant',
    ...defaultSize(3, 3),
    type: 'card',
    icon: '📋',
    iconClass: 'applications-icon',
    description: 'Applicant pipeline count',
  },
  leases: {
    id: 'leases',
    label: 'Active Leases',
    category: 'tenant',
    ...defaultSize(3, 3),
    type: 'card',
    icon: '👥',
    iconClass: 'leases-icon',
    description: 'Active tenants count',
  },
  'recent-applications': {
    id: 'recent-applications',
    label: 'Recent Applications',
    category: 'tenant',
    ...defaultSize(6, 5),
    type: 'panel',
    description: 'Latest applicant activity',
  },
  databases: {
    id: 'databases',
    label: 'Databases (Links)',
    category: 'tenant',
    ...defaultSize(6, 4),
    type: 'panel',
    icon: '📁',
    description: 'Quick links to Properties, Tenants, Applicants, Staff',
  },
  'lease-expiration': {
    id: 'lease-expiration',
    label: 'Lease Expiration Timeline',
    category: 'tenant',
    ...defaultSize(6, 3),
    type: 'panel',
    description: 'Leases expiring in next 6–12 months',
    implemented: false,
  },
  'delinquency-tracker': {
    id: 'delinquency-tracker',
    label: 'Delinquency Tracker',
    category: 'tenant',
    ...defaultSize(4, 2),
    type: 'panel',
    description: 'Overdue rent, days late',
    implemented: false,
  },

  // —— Maintenance ——
  'maintenance-queue': {
    id: 'maintenance-queue',
    label: 'Open Work Orders',
    category: 'maintenance',
    ...defaultSize(6, 3),
    type: 'panel',
    description: 'By priority, assignee, status',
    implemented: false,
  },
  'maintenance-calendar': {
    id: 'maintenance-calendar',
    label: 'Maintenance Calendar',
    category: 'maintenance',
    ...defaultSize(4, 3),
    type: 'panel',
    description: 'Scheduled jobs, inspections',
    implemented: false,
  },

  // —— Legal & Compliance ——
  'legal-cases': {
    id: 'legal-cases',
    label: 'Legal Cases',
    category: 'legal',
    ...defaultSize(6, 4),
    type: 'panel',
    icon: '⚖️',
    description: 'Documents, notices, eviction tracker',
  },
  'document-vault': {
    id: 'document-vault',
    label: 'Document Vault',
    category: 'legal',
    ...defaultSize(6, 4),
    type: 'panel',
    description: 'Leases, addenda, searchable',
    implemented: false,
  },

  // —— Sticky Notes ——
  'sticky-1': {
    id: 'sticky-1',
    label: 'Sticky Note',
    category: 'productivity',
    ...defaultSize(3, 3),
    type: 'sticky',
    description: 'Editable sticky note',
    color: '#fef9c3',
  },
  'sticky-2': {
    id: 'sticky-2',
    label: 'Sticky Note 2',
    category: 'productivity',
    ...defaultSize(3, 3),
    type: 'sticky',
    description: 'Editable sticky note',
    color: '#dbeafe',
  },
  'sticky-3': {
    id: 'sticky-3',
    label: 'Sticky Note 3',
    category: 'productivity',
    ...defaultSize(3, 3),
    type: 'sticky',
    description: 'Editable sticky note',
    color: '#dcfce7',
  },
  'sticky-4': {
    id: 'sticky-4',
    label: 'Sticky Note 4',
    category: 'productivity',
    ...defaultSize(3, 3),
    type: 'sticky',
    description: 'Editable sticky note',
    color: '#fce7f3',
  },
  'sticky-5': {
    id: 'sticky-5',
    label: 'Sticky Note 5',
    category: 'productivity',
    ...defaultSize(3, 3),
    type: 'sticky',
    description: 'Editable sticky note',
    color: '#f3e8ff',
  },
};

/** Widgets that are implemented and render on the dashboard. */
export function getImplementedWidgetIds() {
  return Object.entries(WIDGET_REGISTRY)
    .filter(([, def]) => def.implemented !== false)
    .map(([id]) => id);
}

/** Get bottom-most y + h from items (for placing new widget below). */
export function getNextY(items) {
  if (!items.length) return 0;
  return Math.max(...items.map((it) => it.y + it.h));
}

/** Get default layout item for a widget type (for adding to grid). */
export function getDefaultLayoutItem(widgetId, nextY = 0) {
  const def = WIDGET_REGISTRY[widgetId];
  if (!def) return null;
  return {
    i: widgetId,
    widgetType: widgetId,
    x: 0,
    y: nextY,
    w: def.w,
    h: def.h,
    minW: def.type === 'full' ? 12 : 1,
    minH: 1,
    config: {},
  };
}

/** Default widget id list for initial layout (legacy compatibility). */
export const DEFAULT_WIDGET_IDS = [
  'search',
  'quick-actions',
  'applications',
  'leases',
  'properties',
  'revenue',
  'recent-applications',
  'upcoming-tasks',
];

/** Build initial layout items from a list of widget ids (for default or migration). */
export function buildDefaultItems(widgetIds = DEFAULT_WIDGET_IDS) {
  const items = [];
  const COLS = 12;
  let curX = 0;
  let curY = 0;
  let rowMaxH = 0;
  for (const id of widgetIds) {
    const def = WIDGET_REGISTRY[id];
    if (!def) continue;
    const w = def.w ?? 12;
    const h = def.h ?? 2;
    // If this item doesn't fit on the current row, move to next row
    if (curX + w > COLS) {
      curY += rowMaxH;
      curX = 0;
      rowMaxH = 0;
    }
    items.push({
      i: id,
      widgetType: id,
      x: curX,
      y: curY,
      w,
      h,
      minW: def.type === 'full' ? 12 : 1,
      minH: 1,
      config: {},
    });
    curX += w;
    rowMaxH = Math.max(rowMaxH, h);
  }
  return items;
}

/** Build RGL layout array from our items (strip widgetType/config for react-grid-layout). */
export function itemsToLayout(items) {
  return items.map(({ i, x, y, w, h, minW, minH }) => ({
    i,
    x,
    y,
    w,
    h,
    ...(minW != null && { minW }),
    ...(minH != null && { minH }),
  }));
}

/** Merge RGL layout change back into our items (preserve widgetType, config). */
export function mergeLayoutIntoItems(items, layout) {
  const byI = new Map(items.map((it) => [it.i, { ...it }]));
  layout.forEach(({ i, x, y, w, h }) => {
    if (byI.has(i)) {
      byI.get(i).x = x;
      byI.get(i).y = y;
      byI.get(i).w = w;
      byI.get(i).h = h;
    }
  });
  return Array.from(byI.values());
}

export default WIDGET_REGISTRY;
